/**
 * KES: search analytics for Algolia.
 *
 * One module shared by the results page (algolia-search.js) and the dropdown
 * (algolia-predictive.js), because both surfaces have to agree on the user token, the
 * consent gate, and which search a given product came from.
 *
 * There are two separate jobs here and they are deliberately kept apart, because they
 * answer to different rules:
 *
 *   1. INSIGHTS EVENTS to Algolia (click, add to cart, purchase). These are analytics,
 *      so they are gated on the shopper accepting analytics cookies. Nothing is sent and
 *      no user token is even created before that. Events raised before a decision is made
 *      are held in memory and flushed if the shopper then accepts, so a shopper who
 *      searches, clicks and only then accepts the banner is not lost.
 *
 *   2. ORDER ATTRIBUTION, which is how revenue per search term is worked out. The search
 *      term is written onto the CART, so it travels with the order into Shopify and can
 *      be read back from order data afterwards. That is server-side cart state carrying
 *      the shopper's own context through their own purchase, the same way Shopify itself
 *      records landing page and referrer on every order, so it does not sit behind the
 *      cookie banner. Before the item reaches the cart it is held in sessionStorage only,
 *      which lasts for the browsing session and no longer.
 *
 * Revenue per search term is computed from Shopify ORDER data, not from Algolia. Algolia
 * gates its own revenue reporting behind the revenueAnalytics feature, which is not on
 * this plan:
 *     /2/conversions/purchaseRate -> 401 Feature "revenueAnalytics" not available
 * The Algolia conversion events are still sent, because they feed Algolia's ranking and
 * personalisation models, but they are not where the money number comes from.
 */
(() => {
  // Loaded from both the header search modal (so cart events work site wide) and the
  // search results section. Running twice would double every event and register the cart
  // listener twice, so the second load stops here.
  if (window.KESAlgoliaInsights) return;

  const INSIGHTS_URL = 'https://insights.algolia.io/1/events';
  const TOKEN_KEY = 'kes_algolia_user_token';
  const ATTRIB_KEY = 'kes_search_attribution';
  const CART_ATTRIBUTE = '_kes_search';

  // A shopper who searches, browses a few products and then buys is the normal case, so
  // attribution has to outlive the search page. It does not need to outlive the session:
  // once the product is in the cart the attribution is on the cart, server side.
  const ATTRIB_MAX_AGE_MS = 6 * 60 * 60 * 1000;
  const ATTRIB_MAX_PRODUCTS = 20;

  // Events raised before the shopper has answered the banner. Small on purpose: this is
  // a short grace window, not a way of banking analytics on people who never agree.
  const PENDING_LIMIT = 20;
  const pending = [];

  let config = null;

  /* -------------------------------------------------------------- consent ---- */

  function analyticsAllowed() {
    try {
      return !!window.Shopify?.customerPrivacy?.analyticsProcessingAllowed();
    } catch (e) {
      return false;
    }
  }

  /**
   * Shopify fires this on the document when the shopper answers the banner. Without it,
   * a shopper who accepts after the results have rendered would send nothing for the rest
   * of the page, because every event had already been dropped.
   */
  document.addEventListener('visitorConsentCollected', () => {
    if (!analyticsAllowed()) {
      pending.length = 0;
      return;
    }
    const token = userToken();
    const queued = pending.splice(0, pending.length);
    if (!token) return;
    for (const held of queued) {
      post({ events: [{ ...held.event, userToken: token, timestamp: held.ts }] });
    }
  });

  /* ---------------------------------------------------------------- token ---- */

  /** Created only once analytics are allowed, so declining leaves nothing behind. */
  function userToken() {
    if (!analyticsAllowed()) return null;
    try {
      let token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        token = `anon-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
        localStorage.setItem(TOKEN_KEY, token);
      }
      return token;
    } catch (e) {
      return null; // private browsing, or storage blocked
    }
  }

  /* --------------------------------------------------------------- sending ---- */

  function post(body) {
    if (!config) return;
    // Credentials go in the query string because sendBeacon cannot set headers, and the
    // search-only key is public by design. sendBeacon survives the navigation that a
    // click on a result immediately causes.
    const url =
      `${INSIGHTS_URL}?X-Algolia-Application-Id=${encodeURIComponent(config.appId)}` +
      `&X-Algolia-API-Key=${encodeURIComponent(config.key)}`;
    const payload = JSON.stringify(body);
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      } else {
        fetch(url, {
          method: 'POST',
          body: payload,
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
        });
      }
    } catch (e) {
      /* analytics must never break the interaction it is measuring */
    }
  }

  /** Queue rather than drop while the banner is still unanswered. */
  function send(event) {
    if (!config) return;
    const token = userToken();
    if (!token) {
      // Stamped now rather than at flush time, so an event that is sent after the shopper
      // finally answers the banner still carries the moment it actually happened.
      if (pending.length < PENDING_LIMIT) pending.push({ event, ts: Date.now() });
      return;
    }
    post({ events: [{ ...event, userToken: token }] });
  }

  /* ---------------------------------------------------- attribution store ---- */

  function readStore() {
    try {
      const raw = sessionStorage.getItem(ATTRIB_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      const cutoff = Date.now() - ATTRIB_MAX_AGE_MS;
      const fresh = {};
      for (const [id, rec] of Object.entries(parsed)) {
        if (rec && rec.ts > cutoff) fresh[id] = rec;
      }
      return fresh;
    } catch (e) {
      return {};
    }
  }

  function writeStore(store) {
    try {
      const entries = Object.entries(store)
        .sort((a, b) => b[1].ts - a[1].ts)
        .slice(0, ATTRIB_MAX_PRODUCTS);
      sessionStorage.setItem(ATTRIB_KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch (e) {
      /* storage blocked: attribution is lost, the shop is not */
    }
  }

  /**
   * Record that this product was reached from this search. The most recent search wins,
   * which is the honest reading: if a shopper searched twice and clicked the same product
   * the second time, the second search is the one that sold it.
   *
   * @param {string} productId  Shopify product id, which is also the Algolia objectID
   * @param {{term: string, queryID?: string, index: string, position?: number}} search
   */
  function remember(productId, search) {
    if (!productId || !search?.term) return;
    const store = readStore();
    store[String(productId)] = {
      t: search.term,
      q: search.queryID || null,
      i: search.index,
      ts: Date.now(),
    };
    writeStore(store);
  }

  function lookup(productId) {
    return readStore()[String(productId)] || null;
  }

  /* ------------------------------------------------------- cart attribution ---- */

  let syncTimer = null;
  const cartEventsSent = new Set();

  /**
   * Copy the attribution for whatever is now in the cart onto the cart itself, so it
   * survives into the order, and raise the Algolia add to cart event for anything new.
   *
   * Both jobs read the same `/cart.js` because the cart is the only place with the real
   * line price and quantity. The theme's `cart:update` event carries the total number of
   * items in the cart, not how many of this product were added, so it cannot be used for
   * the quantity.
   *
   * Runs as its own request AFTER the theme's add to cart has finished. It deliberately
   * does not touch the add to cart request itself: a bug in analytics must not be able to
   * stop anybody buying anything. Attributes are invisible to the shopper, so writing one
   * changes nothing on screen.
   */
  async function syncCart() {
    try {
      const store = readStore();
      if (!Object.keys(store).length) return;

      const cartUrl = window.Theme?.routes?.cart_url || '/cart';
      const cart = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null));
      if (!cart?.items?.length) return;

      let existing = {};
      try {
        existing = JSON.parse(cart.attributes?.[CART_ATTRIBUTE] || '{}');
      } catch (e) {
        existing = {};
      }

      const merged = { ...existing };
      let changed = false;

      for (const item of cart.items) {
        const id = String(item.product_id);
        const rec = store[id];
        if (!rec) continue;

        // First search to put a product in the cart is the one that sold it, so an
        // existing entry is never overwritten by a later search for the same product.
        if (!merged[id]) {
          merged[id] = { t: rec.t, q: rec.q, i: rec.i };
          changed = true;
        }

        if (!cartEventsSent.has(id)) {
          cartEventsSent.add(id);
          addedToCart({
            objectID: id,
            index: rec.i,
            queryID: rec.q,
            price: typeof item.final_price === 'number' ? item.final_price / 100 : undefined,
            quantity: item.quantity,
          });
        }
      }

      if (!changed) return;

      const updateUrl = window.Theme?.routes?.cart_update_url || '/cart/update';
      await fetch(updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [CART_ATTRIBUTE]: JSON.stringify(merged) } }),
      });
    } catch (e) {
      /* attribution is best effort and must never interrupt a purchase */
    }
  }

  /* ---------------------------------------------------------------- events ---- */

  /**
   * A result was clicked. Position is 1-indexed across the whole result set, and the
   * queryID must be the one from the page that actually returned this product: on the
   * results page a shopper can load several pages before clicking, and attributing a
   * page 1 click to the page 3 query makes the click position meaningless.
   */
  function clicked({ objectID, position, queryID, index, term }) {
    remember(objectID, { term, queryID, index, position });
    if (!queryID) return;
    send({
      eventType: 'click',
      eventName: 'Product Clicked After Search',
      index,
      objectIDs: [String(objectID)],
      queryID,
      positions: [position],
    });
  }

  /**
   * Added to cart after a search. Sent with price and quantity so Algolia can value it,
   * even though the reportable revenue figure comes from Shopify order data.
   */
  function addedToCart({ objectID, index, queryID, price, quantity }) {
    if (!index) return;
    const objectData = { quantity: quantity || 1 };
    if (typeof price === 'number' && price > 0) objectData.price = price;
    if (queryID) objectData.queryID = queryID;

    send({
      eventType: 'conversion',
      eventSubtype: 'addToCart',
      eventName: 'Product Added To Cart After Search',
      index,
      objectIDs: [String(objectID)],
      objectData: [objectData],
      currency: window.Shopify?.currency?.active || 'GBP',
    });
  }

  window.KESAlgoliaInsights = {
    /** Called by whichever surface loads first; both pass the same credentials. */
    configure(cfg) {
      if (!config && cfg?.appId && cfg?.key) config = cfg;
    },
    analyticsAllowed,
    remember,
    lookup,
    clicked,
    CART_ATTRIBUTE,
  };

  /*
   * The theme announces every cart change on `cart:update`, so the attribution sync can
   * hang off that rather than wrapping the cart requests themselves. Debounced because a
   * quick-add fires the event more than once while the drawer re-renders.
   */
  document.addEventListener('cart:update', () => {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncCart, 400);
  });

  /*
   * Catch up on load as well. A cart can be filled by a route that does not raise
   * `cart:update` (a plain form post, or a page that reloads straight after adding), and
   * a write that failed once would otherwise never be retried. Costs nothing for the vast
   * majority of visitors, because syncCart stops before fetching the cart unless this
   * shopper has actually clicked a search result.
   */
  setTimeout(syncCart, 1200);
})();
